import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { toast } from "sonner";

/**
 * Single-artist cart. Enforces the rule that every item in the cart
 * belongs to the same artist (required by our destination-charge routing).
 * Cart is persisted to localStorage under `stardomfans_cart_v1`.
 */

const STORAGE_KEY = "stardomfans_cart_v1";
const CartContext = createContext(null);

const readCart = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { artist_id: null, artist_name: null, items: [] };
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.items)) {
      return { artist_id: null, artist_name: null, items: [] };
    }
    return parsed;
  } catch {
    return { artist_id: null, artist_name: null, items: [] };
  }
};

const writeCart = (cart) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
  } catch (e) {
    console.error("Cart persist failed:", e);
  }
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => readCart());
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    writeCart(cart);
  }, [cart]);

  const addItem = useCallback((product, quantity = 1) => {
    if (!product || !product.product_id || !product.artist_id) {
      toast.error("Invalid product");
      return false;
    }
    if (quantity <= 0) return false;

    // Enforce single-artist cart
    if (cart.artist_id && cart.artist_id !== product.artist_id) {
      const proceed = window.confirm(
        `Your cart already has items from ${cart.artist_name || "another artist"}. ` +
        `Clear it and start a new cart for ${product.artist_name || "this artist"}?`
      );
      if (!proceed) return false;
      setCart({
        artist_id: product.artist_id,
        artist_name: product.artist_name || null,
        items: [{
          product_id: product.product_id,
          name: product.name,
          price: parseFloat(product.price) || 0,
          image_url: product.image_url || null,
          category: product.category || null,
          requires_shipping: !!product.requires_shipping,
          quantity,
        }],
      });
      toast.success(`Cart reset. Added ${product.name}`);
      return true;
    }

    setCart((prev) => {
      const existing = prev.items.find((it) => it.product_id === product.product_id);
      let items;
      if (existing) {
        items = prev.items.map((it) =>
          it.product_id === product.product_id
            ? { ...it, quantity: it.quantity + quantity }
            : it
        );
      } else {
        items = [
          ...prev.items,
          {
            product_id: product.product_id,
            name: product.name,
            price: parseFloat(product.price) || 0,
            image_url: product.image_url || null,
            category: product.category || null,
            requires_shipping: !!product.requires_shipping,
            quantity,
          },
        ];
      }
      return {
        artist_id: product.artist_id,
        artist_name: product.artist_name || prev.artist_name,
        items,
      };
    });
    toast.success(`${product.name} added to cart`);
    return true;
  }, [cart.artist_id, cart.artist_name]);

  const updateQuantity = useCallback((product_id, quantity) => {
    setCart((prev) => {
      if (quantity <= 0) {
        const items = prev.items.filter((it) => it.product_id !== product_id);
        return items.length === 0
          ? { artist_id: null, artist_name: null, items: [] }
          : { ...prev, items };
      }
      return {
        ...prev,
        items: prev.items.map((it) =>
          it.product_id === product_id ? { ...it, quantity } : it
        ),
      };
    });
  }, []);

  const removeItem = useCallback((product_id) => {
    setCart((prev) => {
      const items = prev.items.filter((it) => it.product_id !== product_id);
      return items.length === 0
        ? { artist_id: null, artist_name: null, items: [] }
        : { ...prev, items };
    });
  }, []);

  const clearCart = useCallback(() => {
    setCart({ artist_id: null, artist_name: null, items: [] });
  }, []);

  const itemCount = cart.items.reduce((sum, it) => sum + it.quantity, 0);
  const subtotal = cart.items.reduce((sum, it) => sum + it.price * it.quantity, 0);
  const requiresShipping = cart.items.some((it) => it.requires_shipping);

  return (
    <CartContext.Provider
      value={{
        cart,
        itemCount,
        subtotal,
        requiresShipping,
        drawerOpen,
        setDrawerOpen,
        addItem,
        updateQuantity,
        removeItem,
        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
